import { useState, useRef, useEffect } from "react";
import { Link, Navigate, useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import AuthLayout from "../components/auth/AuthLayout";
import LaunchingSoonCard from "../components/auth/LaunchingSoonCard";
import RequestAccessCard from "../components/auth/RequestAccessCard";
import TurnstileWidget, { type TurnstileWidgetRef } from "../components/auth/TurnstileWidget";
import { googleAuth } from "../api/auth";

export default function LoginPage() {
  const { user, login, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [successMsg, setSuccessMsg] = useState("");

  const searchParams = new URLSearchParams(location.search);
  // By default show Sign In. Show Request Access if ?request=true or ?mode=request-access
  const [showRequestAccess, setShowRequestAccess] = useState(
    searchParams.get("request") === "true" || searchParams.get("mode") === "request-access"
  );

  const turnstileRef = useRef<TurnstileWidgetRef>(null);
  const googleBtnContainerRef = useRef<HTMLDivElement>(null);
  const [googleBtnReady, setGoogleBtnReady] = useState(false);

  useEffect(() => {
    // Check if redirected from reset-password or registration
    const state = location.state as { message?: string } | null;
    if (state?.message) {
      setSuccessMsg(state.message);
    }
  }, [location.state]);

  const handleGoogleCredentialResponse = async (response: { credential: string }) => {
    setLoading(true);
    try {
      const authRes = await googleAuth(response.credential);
      localStorage.setItem("access_token", authRes.access_token);
      localStorage.setItem("role", authRes.role);
      await refreshUser();
      if (authRes.role === "admin" || authRes.role === "super_admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      const gErr = err as { response?: { data?: { detail?: string } }; message?: string };
      setError(gErr.response?.data?.detail || gErr.message || "Google authentication failed.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!googleClientId) return;

    let mounted = true;
    const checkAndInit = () => {
      const g = (window as unknown as { google?: { accounts?: { id?: { initialize: Function; renderButton: Function; prompt: Function } } } }).google;
      if (g?.accounts?.id && googleBtnContainerRef.current && mounted) {
        try {
          g.accounts.id.initialize({
            client_id: googleClientId,
            callback: handleGoogleCredentialResponse,
            auto_select: false,
            cancel_on_tap_outside: true,
          });

          g.accounts.id.renderButton(googleBtnContainerRef.current, {
            theme: "filled_blue",
            size: "large",
            text: "continue_with",
            shape: "rectangular",
            width: 360,
          });
          setGoogleBtnReady(true);
        } catch (e) {
          console.warn("Google GIS initialization error:", e);
        }
        return true;
      }
      return false;
    };

    if (!checkAndInit()) {
      const timer = setInterval(() => {
        if (checkAndInit()) {
          clearInterval(timer);
        }
      }, 300);
      return () => {
        mounted = false;
        clearInterval(timer);
      };
    }
    return () => {
      mounted = false;
    };
  }, []);

  const handleGoogleClick = async () => {
    const googleClientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
    if (!googleClientId) {
      setError("Google Sign-In is not currently configured for this domain.");
      return;
    }

    if (typeof window !== "undefined" && (window as unknown as { google?: { accounts?: { id?: unknown } } }).google) {
      setError("");
      try {
        const googleObj = (window as unknown as { google: { accounts: { id: { initialize: Function; prompt: Function } } } }).google;
        googleObj.accounts.id.initialize({
          client_id: googleClientId,
          callback: handleGoogleCredentialResponse,
        });
        googleObj.accounts.id.prompt((notification: { isNotDisplayed: () => boolean; getNotDisplayedReason: () => string }) => {
          if (notification.isNotDisplayed()) {
            const reason = notification.getNotDisplayedReason();
            console.warn("Google One Tap prompt not displayed:", reason);
          }
        });
      } catch (e) {
        console.error("Google sign-in error", e);
        setError("Unable to initialize Google Sign-In.");
      }
    } else {
      setError("Google authentication service is loading. Please try again momentarily.");
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email || !password) {
      setError("Please enter both email and password.");
      return;
    }

    setLoading(true);
    setError("");
    setSuccessMsg("");

    try {
      const role = await login(email, password, turnstileToken);
      if (role === "admin" || role === "super_admin") {
        navigate("/admin");
      } else {
        navigate("/dashboard");
      }
    } catch (err: unknown) {
      const axiosErr = err as {
        response?: {
          status?: number;
          data?: {
            detail?: string;
            code?: string;
            email?: string;
          };
        };
        message?: string;
      };

      const detail = axiosErr.response?.data?.detail || "";
      const code = axiosErr.response?.data?.code || "";

      // If email is unverified, seamlessly redirect to OTP verification screen
      if (code === "EMAIL_NOT_VERIFIED" || detail.toLowerCase().includes("verify your email")) {
        navigate(`/verify-email?email=${encodeURIComponent(email)}`, {
          state: { message: "Your email is not verified yet. Please enter the verification code sent to your inbox." },
        });
        return;
      }

      setError(detail || axiosErr.message || "Invalid credentials. Please try again.");
      turnstileRef.current?.reset();
      setTurnstileToken("");
    } finally {
      setLoading(false);
    }
  };

  if (user) {
    if (user.role === "admin" || user.role === "super_admin") {
      return <Navigate to="/admin" replace />;
    }
    return <Navigate to="/dashboard" replace />;
  }

  const isAccessRequestMode = import.meta.env.VITE_ACCESS_REQUEST_MODE === "true";
  const isLaunchMode = import.meta.env.VITE_LAUNCH_MODE === "true";

  if (isAccessRequestMode && showRequestAccess) {
    return (
      <AuthLayout>
        <RequestAccessCard onSwitchToSignIn={() => setShowRequestAccess(false)} />
      </AuthLayout>
    );
  }

  if (isLaunchMode && !isAccessRequestMode) {
    return (
      <AuthLayout>
        <LaunchingSoonCard />
      </AuthLayout>
    );
  }

  return (
    <AuthLayout>
      <h2 className="tx-auth-form-title">Sign in</h2>
      <p className="tx-auth-form-subtitle">
        {isAccessRequestMode
          ? "Enter your credentials to access your Threatactix account"
          : "View latest updates and developments in CTI"}
      </p>

      {successMsg && (
        <div className="tx-auth-alert tx-auth-alert-success" role="status">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
            <polyline points="22 4 12 14.01 9 11.01" />
          </svg>
          <span>{successMsg}</span>
        </div>
      )}

      {error && (
        <div className="tx-auth-alert tx-auth-alert-error" role="alert">
          <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" />
            <line x1="12" y1="8" x2="12" y2="12" />
            <line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          <span>{error}</span>
        </div>
      )}

      <form className="tx-auth-form" onSubmit={handleSubmit} noValidate>
        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-email">Email</label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-email"
              type="email"
              autoComplete="username"
              required
              placeholder="name@company.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
            />
          </div>
        </div>

        <div className="tx-auth-field">
          <label className="tx-auth-label" htmlFor="tx-password">Password</label>
          <div className="tx-auth-input-wrapper">
            <input
              id="tx-password"
              type={showPassword ? "text" : "password"}
              autoComplete="current-password"
              required
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className="tx-auth-input"
              disabled={loading}
            />
            <button
              type="button"
              className="tx-auth-eye-btn"
              onClick={() => setShowPassword(!showPassword)}
              aria-label={showPassword ? "Hide password" : "Show password"}
              tabIndex={-1}
            >
              {showPassword ? (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                  <line x1="1" y1="1" x2="23" y2="23" />
                </svg>
              ) : (
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                  <circle cx="12" cy="12" r="3" />
                </svg>
              )}
            </button>
          </div>
        </div>

        <Link to="/forgot-password" className="tx-auth-link tx-auth-forgot-link">
          Forgot password?
        </Link>

        {/* Turnstile Bot Protection */}
        <TurnstileWidget
          ref={turnstileRef}
          onSuccess={(token) => setTurnstileToken(token)}
          onError={() => setTurnstileToken("")}
          onExpire={() => setTurnstileToken("")}
        />

        <button
          type="submit"
          className="tx-auth-btn-primary"
          disabled={loading}
          id="btn-sign-in"
        >
          {loading ? (
            <>
              <svg className="animate-spin" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" strokeOpacity="0.25" />
                <path d="M12 2a10 10 0 0 1 10 10" />
              </svg>
              <span>Authenticating...</span>
            </>
          ) : (
            "Sign in"
          )}
        </button>

        {!isAccessRequestMode && (
          <>
            <div className="tx-auth-divider">or</div>

            <div style={{ width: "100%", display: "flex", flexDirection: "column", alignItems: "center" }}>
              <div
                ref={googleBtnContainerRef}
                id="g-signin-rendered"
                style={{ width: "100%", display: googleBtnReady ? "flex" : "none", justifyContent: "center" }}
              />
              {!googleBtnReady && (
                <button
                  type="button"
                  onClick={handleGoogleClick}
                  className="tx-auth-btn-google"
                  disabled={loading}
                  id="btn-google-sign-in"
                >
                  <div className="tx-auth-google-badge">
                    <svg width="15" height="15" viewBox="0 0 24 24">
                      <path
                        fill="#4285F4"
                        d="M23.745 12.27c0-.7-.06-1.4-.19-2.07H12v4.51h6.6c-.29 1.52-1.14 2.82-2.4 3.68v3.05h3.88c2.27-2.09 3.665-5.17 3.665-9.17z"
                      />
                      <path
                        fill="#34A853"
                        d="M12 24c3.24 0 5.95-1.08 7.93-2.91l-3.88-3.05c-1.08.72-2.45 1.16-4.05 1.16-3.12 0-5.77-2.1-6.72-4.93H1.25v3.15C3.26 21.36 7.33 24 12 24z"
                      />
                      <path
                        fill="#FBBC05"
                        d="M5.28 14.27c-.25-.72-.38-1.49-.38-2.27s.13-1.55.38-2.27V6.58H1.25C.45 8.17 0 9.99 0 12s.45 3.83 1.25 5.42l4.03-3.15z"
                      />
                      <path
                        fill="#EA4335"
                        d="M12 4.75c1.77 0 3.35.61 4.6 1.8l3.42-3.42C17.95 1.19 15.24 0 12 0 7.33 0 3.26 2.64 1.25 6.58l4.03 3.15c.95-2.83 3.6-4.98 6.72-4.98z"
                      />
                    </svg>
                  </div>
                  <span>Continue with Google</span>
                </button>
              )}
            </div>

          </>
        )}

        <div className="tx-auth-card-footer" style={{ marginTop: 24, textAlign: "center" }}>
          Don't have an account?{" "}
          {isAccessRequestMode ? (
            <button
              type="button"
              id="btn-show-request-access"
              onClick={() => setShowRequestAccess(true)}
              className="tx-auth-link"
              style={{
                background: "none",
                border: "none",
                color: "#10b981",
                fontWeight: 600,
                fontSize: "inherit",
                cursor: "pointer",
                padding: 0,
                textDecoration: "underline",
              }}
            >
              Request Access
            </button>
          ) : (
            <Link to="/register" className="tx-auth-link">
              Sign up
            </Link>
          )}
        </div>
      </form>
    </AuthLayout>
  );
}
